import React, { useEffect, useState } from "react";
import { IResourceComponentsProps, useTranslate } from "@pankod/refine-core";
import { List, Button, Input, DatePicker, notification, Modal } from "@pankod/refine-antd";
import axios from "axios";
import { authProvider } from "authProvider";
import dayjs from "dayjs";

const axiosInstance = axios.create();

axiosInstance.interceptors.request.use((config) => {
  config.headers = {
    Authorization: `Bearer ${localStorage.getItem("ACCESS_TOKEN")}`,
    Accept: "application/json; charset=utf-8",
    "Content-Type": "application/json; charset=utf-8",
  };
  config.timeout = 300000;
  return config;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      await authProvider.refresh({});
      return axiosInstance(originalRequest);
    }
    return Promise.reject(error);
  }
);

interface TableConfig {
  key: string;
  title: string;
  suites: string[];
}

const TABLES: TableConfig[] = [
  {
    key: "Parsem\u00e9es", title: "Parsem\u00e9es",
    suites: ["Nova", "Origine", "Hyggelig", "Bellecour", "Opale", "Jungle",
      "Balan\u00e7oire", "Sienna", "Entre soi", "Confidence", "Cachette",
      "Eden", "Pr\u00e9cieuse", "Clotilde", "Monsieur", "Nuances",
      "Esmeralda", "Op\u00e9ra", "Scala", "Masala", "Georges"],
  },
  { key: "P\u00e9rouges", title: "P\u00e9rouges", suites: ["Duchesse", "M\u00e9daillon", "Acanthe", "Lys", "Arabesque", "Louise"] },
  { key: "Gailleton", title: "Gailleton", suites: ["Rendez-vous", "Fauve", "Envol", "Vertigo", "Myst\u00e8re", "Copper", "Paradis", "Merveille", "Blush", "James"] },
  { key: "54 bis Sala (1)", title: "54 bis Sala", suites: ["Topaze", "Onyx", "Agate", "Nacre", "Rubis"] },
  { key: "54 bis Sala (2)", title: "54 bis Sala", suites: ["Grenat", "Perle", "Jaspe", "Platine"] },
  { key: "Tour Rose", title: "Tour Rose", suites: ["Ocre", "Eb\u00e8ne", "Terracotta", "Acquaverde", "Mia Stanza", "Ambre", "Alba", "Volatili", "Bianca", "Colletore", "Palazzo", "Amarante", "Palma", "Cobalt", "Maison Rose"] },
  { key: "Gourguillon", title: "Gourguillon", suites: ["Divine", "Celeste", "Satin", "Luna", "Velours", "Stella", "Aura", "Osmose"] },
  { key: "Cuvier", title: "Cuvier", suites: ["Dona", "Marlow", "Villari", "N\u00e9roli", "Palladium", "Rom\u00e9o"] },
  { key: "Chamb\u00e9ry", title: "Chamb\u00e9ry", suites: ["Berlingot", "Confiture", "Madeleine", "Verveine", "Porcelaine"] },
  { key: "Cannes", title: "Cannes", suites: ["After Sun", "Golden Hour", "Sunset"] },
];

interface Member {
  id: number;
  name: string;
  color: string;
}

interface SuiteAssignment {
  member_id: number;
  member_name: string;
  color: string;
}

const DEFAULT_COLORS = [
  "#3b82f6", "#ef4444", "#22c55e", "#f59e0b", "#8b5cf6",
  "#ec4899", "#06b6d4", "#f97316", "#6366f1", "#14b8a6",
];

export const AssignmentsList: React.FC<IResourceComponentsProps> = () => {
  const t = useTranslate();
  const [date, setDate] = useState<string>(dayjs().format("YYYY-MM-DD"));
  const [members, setMembers] = useState<Member[]>([]);
  const [assignments, setAssignments] = useState<Record<string, SuiteAssignment>>({});
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [newMemberName, setNewMemberName] = useState("");
  const [newMemberColor, setNewMemberColor] = useState(DEFAULT_COLORS[0]);
  const [activeTab, setActiveTab] = useState<"assignments" | "notes">("assignments");
  const [notes, setNotes] = useState<Record<string, string>>({});
  const saveTimers = React.useRef<Record<string, any>>({});

  const apiUrl = process.env?.REACT_APP_API_URL || "";

  const fetchMembers = async () => {
    try {
      const res = await axiosInstance.get(`${apiUrl}/api/liveboard-members`);
      setMembers(res.data?.data || []);
    } catch (err) {
      console.error("Failed to fetch members", err);
    }
  };

  const fetchAssignments = async (targetDate: string) => {
    try {
      const res = await axiosInstance.get(`${apiUrl}/api/liveboard-assignments?date=${targetDate}`);
      setAssignments(res.data?.data || {});
    } catch (err) {
      console.error("Failed to fetch assignments", err);
    }
  };

  const fetchNotes = async (targetDate: string) => {
    try {
      const res = await axiosInstance.get(`${apiUrl}/api/liveboard-notes?date=${targetDate}`);
      setNotes(res.data?.data || {});
    } catch (err) {
      console.error("Failed to fetch notes", err);
    }
  };

  const handleNoteChange = (suiteName: string, value: string) => {
    setNotes((prev) => ({ ...prev, [suiteName]: value }));
    if (saveTimers.current[suiteName]) clearTimeout(saveTimers.current[suiteName]);
    saveTimers.current[suiteName] = setTimeout(async () => {
      try {
        await axiosInstance.post(`${apiUrl}/api/liveboard-notes`, {
          date,
          suite_name: suiteName,
          note: value,
        });
      } catch (err) {
        console.error("Failed to save note", err);
      }
    }, 800);
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  useEffect(() => {
    fetchAssignments(date);
    fetchNotes(date);
  }, [date]);

  const handleAddMember = async () => {
    if (!newMemberName.trim()) return;
    try {
      await axiosInstance.post(`${apiUrl}/api/liveboard-members`, {
        name: newMemberName.trim(),
        color: newMemberColor,
      });
      setNewMemberName("");
      setNewMemberColor(DEFAULT_COLORS[(members.length + 1) % DEFAULT_COLORS.length]);
      fetchMembers();
    } catch (err) {
      notification.error({ message: "Erreur", placement: "topRight" });
    }
  };

  const handleDeleteMember = async (id: number) => {
    try {
      await axiosInstance.delete(`${apiUrl}/api/liveboard-members?id=${id}`);
      if (selectedMember?.id === id) setSelectedMember(null);
      fetchMembers();
      fetchAssignments(date);
    } catch (err) {
      notification.error({ message: "Erreur", placement: "topRight" });
    }
  };

  const [isPainting, setIsPainting] = useState(false);
  const [paintMode, setPaintMode] = useState<"assign" | "remove">("assign");
  const paintedRef = React.useRef<Set<string>>(new Set());

  const assignSuite = async (suiteName: string, memberId: number | null) => {
    try {
      await axiosInstance.post(`${apiUrl}/api/liveboard-assignments`, {
        date,
        suite_name: suiteName,
        member_id: memberId,
      });
    } catch (err) {
      // Silent fail during painting
    }
  };

  const handleSuiteDown = (suiteName: string) => {
    if (!selectedMember) return;
    const current = assignments[suiteName];
    const isAlreadyAssigned = current?.member_id === selectedMember.id;

    setIsPainting(true);
    paintedRef.current = new Set([suiteName]);

    if (isAlreadyAssigned) {
      setPaintMode("remove");
      setAssignments((prev) => {
        const next = { ...prev };
        delete next[suiteName];
        return next;
      });
      assignSuite(suiteName, null);
    } else {
      setPaintMode("assign");
      setAssignments((prev) => ({
        ...prev,
        [suiteName]: {
          member_id: selectedMember.id,
          member_name: selectedMember.name,
          color: selectedMember.color,
        },
      }));
      assignSuite(suiteName, selectedMember.id);
    }
  };

  const handleSuiteEnter = (suiteName: string) => {
    if (!isPainting || !selectedMember || paintedRef.current.has(suiteName)) return;
    paintedRef.current.add(suiteName);

    if (paintMode === "remove") {
      setAssignments((prev) => {
        const next = { ...prev };
        delete next[suiteName];
        return next;
      });
      assignSuite(suiteName, null);
    } else {
      setAssignments((prev) => ({
        ...prev,
        [suiteName]: {
          member_id: selectedMember.id,
          member_name: selectedMember.name,
          color: selectedMember.color,
        },
      }));
      assignSuite(suiteName, selectedMember.id);
    }
  };

  useEffect(() => {
    const handleMouseUp = () => setIsPainting(false);
    window.addEventListener("mouseup", handleMouseUp);
    return () => window.removeEventListener("mouseup", handleMouseUp);
  }, []);

  return (
    <List title="Assignations CM">
      {/* Date picker */}
      <div style={{ marginBottom: 20 }}>
        <DatePicker
          value={dayjs(date)}
          onChange={(val) => { if (val) setDate(val.format("YYYY-MM-DD")); }}
          style={{ width: 200 }}
        />
      </div>

      {/* Members bar */}
      <div style={{ marginBottom: 20, padding: 16, background: "#fafafa", borderRadius: 8, border: "1px solid #d9d9d9" }}>
        <div style={{ fontWeight: 700, marginBottom: 12, fontSize: 14 }}>Membres de l'équipe</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
          {members.map((m) => (
            <div
              key={m.id}
              onClick={() => setSelectedMember(selectedMember?.id === m.id ? null : m)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 14px",
                borderRadius: 20,
                cursor: "pointer",
                background: selectedMember?.id === m.id ? m.color : "#ffffff",
                color: selectedMember?.id === m.id ? "#ffffff" : "#333333",
                border: `2px solid ${m.color}`,
                fontWeight: 600,
                fontSize: 13,
                transition: "all 0.15s ease",
              }}
            >
              <span
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  background: selectedMember?.id === m.id ? "#ffffff" : m.color,
                  display: "inline-block",
                }}
              />
              {m.name}
              <span
                onClick={(e) => { e.stopPropagation(); handleDeleteMember(m.id); }}
                style={{ marginLeft: 4, cursor: "pointer", opacity: 0.5, fontSize: 11 }}
              >
                ✕
              </span>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Input
            placeholder="Nouveau membre"
            value={newMemberName}
            onChange={(e) => setNewMemberName(e.target.value)}
            onPressEnter={handleAddMember}
            style={{ width: 180 }}
          />
          <input
            type="color"
            value={newMemberColor}
            onChange={(e) => setNewMemberColor(e.target.value)}
            style={{ width: 36, height: 36, border: "none", cursor: "pointer", borderRadius: 4 }}
          />
          <Button type="primary" onClick={handleAddMember}>Ajouter</Button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 0, marginBottom: 16 }}>
        {[
          { key: "assignments" as const, label: "Assignations" },
          { key: "notes" as const, label: "Notes Extras" },
        ].map((tab) => (
          <div
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: "8px 24px",
              cursor: "pointer",
              fontWeight: activeTab === tab.key ? 700 : 400,
              borderBottom: activeTab === tab.key ? "3px solid #1a1a2e" : "3px solid transparent",
              color: activeTab === tab.key ? "#1a1a2e" : "#999",
              fontSize: 14,
            }}
          >
            {tab.label}
          </div>
        ))}
      </div>

      {activeTab === "assignments" && (
        <>
          {/* Instructions */}
          {selectedMember && (
            <div style={{ marginBottom: 16, padding: "8px 16px", background: selectedMember.color + "20", border: `1px solid ${selectedMember.color}`, borderRadius: 8, fontSize: 13 }}>
              Mode pinceau : <strong style={{ color: selectedMember.color }}>{selectedMember.name}</strong> — Cliquez sur les suites pour assigner. Re-cliquez pour désassigner.
            </div>
          )}
        </>
      )}

      {/* Tables with suites */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, userSelect: "none" }}>
        {TABLES.map((table) => (
          <div
            key={table.key}
            style={{
              background: "#ffffff",
              border: "1px solid #d9d9d9",
              borderRadius: 8,
              overflow: "hidden",
            }}
          >
            <div style={{
              background: "#1a1a2e",
              color: "#ffffff",
              padding: "6px 12px",
              fontWeight: 700,
              fontSize: 13,
              textAlign: "center",
              letterSpacing: 1,
              textTransform: "uppercase",
            }}>
              {table.title}
            </div>
            <div style={{ padding: 4 }}>
              {table.suites.map((suite) => {
                if (activeTab === "notes") {
                  return (
                    <div
                      key={suite}
                      style={{
                        padding: "3px 10px",
                        margin: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        fontSize: 13,
                      }}
                    >
                      <span style={{ fontWeight: 500, minWidth: 100 }}>{suite}</span>
                      <Input
                        size="small"
                        placeholder="Note extra..."
                        value={notes[suite] || ""}
                        onChange={(e) => handleNoteChange(suite, e.target.value)}
                        style={{ flex: 1, fontSize: 12 }}
                      />
                    </div>
                  );
                }
                const assignment = assignments[suite];
                return (
                  <div
                    key={suite}
                    onMouseDown={() => handleSuiteDown(suite)}
                    onMouseEnter={() => handleSuiteEnter(suite)}
                    style={{
                      padding: "4px 10px",
                      margin: 2,
                      borderRadius: 4,
                      cursor: selectedMember ? "pointer" : "default",
                      background: assignment ? assignment.color + "25" : "transparent",
                      borderLeft: assignment ? `4px solid ${assignment.color}` : "4px solid transparent",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      fontSize: 13,
                      transition: "all 0.15s ease",
                    }}
                  >
                    <span style={{ fontWeight: 500 }}>{suite}</span>
                    {assignment && (
                      <span style={{ fontSize: 11, color: assignment.color, fontWeight: 600 }}>
                        {assignment.member_name}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </List>
  );
};
